import * as React from 'react';
import { connect } from 'react-redux';
import { Link, RouteComponentProps } from 'react-router-dom';
import { ApplicationState } from '../../store';
import { ToxnotComponent, ToxnotComponentProperties } from '../core/BaseApplicationComponents/ToxnotComponent';
import { ToxnotServiceClient } from '../../store/ToxnotServiceClient';
import { SharedMaterialsFullCompany } from '../../store/Models/SharedMaterials/DataModel';
import { ToxnotReactComponentPaths } from '../core/ToxnotReactComponentPaths';
import { LoadingSpinner, LoadingSpinnerSize } from '../core/LoadingSpinner';
import { CreateReportRequest, ReportTypeWithContext } from '../../store/Models/Api/CustomerApiRequestsAndResponses';
import { ToxnotReactStatusComponent, ToxnotReactStatusComponentState } from '../core/ToxnotReactStatusComponent';

import './MyCustomers.css';
import { ReportContext } from '../../store/Models/Customers/Customer';
import { ReportTypesManager } from '../../store/Managers/ReportTypesManager';
import { ReportTypeSelector } from '../core/ReportTypeSelector';
import { DynamicRegulation } from '../../store/Models/Compliance/DynamicRegulation';

enum RequestReportStatus {
    Loading,
    CompanyFound,
    CompanyNotFound,
    RequestSending,
    RequestSent,
    RequestFailed
}

interface RequestReportsState {
    status: RequestReportStatus;
    requesteeCompany: SharedMaterialsFullCompany | null;
    dynRegs: DynamicRegulation[];
    reportTypesToRegulations: { [reportType: string]: string[] };
    selectedReportTypes: { [reportType: string]: ReportContext | null};
    reportOnAllPurchases: boolean;
    reportTypesAndRegulationsToSpecificInfo: {[reportTypeOrRegulation: string]: string}
}

type RequestReportsProps =
    ToxnotComponentProperties &
    RouteComponentProps<{ customUrlName: string }>;

// ===============================================================================

class RequestReports extends ToxnotComponent<RequestReportsProps, RequestReportsState> {
    private _serviceClient: ToxnotServiceClient;
    private _reportTypesManager: ReportTypesManager;

    constructor(props: RequestReportsProps) {
        super(props);

        this._serviceClient = new ToxnotServiceClient();
        this._reportTypesManager = new ReportTypesManager();

        this.state = {
            status: RequestReportStatus.Loading,
            requesteeCompany: null,
            selectedReportTypes: {},
            reportOnAllPurchases: false,
            reportTypesAndRegulationsToSpecificInfo: {},
            dynRegs: [],
            reportTypesToRegulations: {}
        };
    }

    // ===============================================================================

    private _statusToToxnotStatusComponentStateMap = {
        [RequestReportStatus.RequestSending]: { state: ToxnotReactStatusComponentState.BeginOperation, message: this.localizeString("Sending...") },
        [RequestReportStatus.RequestSent]: { state: ToxnotReactStatusComponentState.OperationSucceeded, message: this.localizeString("Your request was submitted! You'll receive an email when your reports are ready.") },
        [RequestReportStatus.RequestFailed]: { state: ToxnotReactStatusComponentState.OperationFailed, message: this.localizeString("Sorry, there was an issue sending your requests.") },
        [RequestReportStatus.Loading]: { state: ToxnotReactStatusComponentState.Normal, message: "" },
        [RequestReportStatus.CompanyFound]: { state: ToxnotReactStatusComponentState.Normal, message: "" },
        [RequestReportStatus.CompanyNotFound]: { state: ToxnotReactStatusComponentState.Normal, message: "" },
    };

    // ===============================================================================

    public componentDidMount() {
        this.getCompanyFromUrlName();
    }

    // ===============================================================================

    public render() {
        let body: JSX.Element;

        if (this.state.status === RequestReportStatus.Loading) {
            body = this.renderLoading();
        }
        else if (this.state.status === RequestReportStatus.CompanyNotFound) {
            body = this.renderNoCompanyFoundMessage();
        }
        else if (this.state.status === RequestReportStatus.CompanyFound) {
            if (this.state.requesteeCompany === null) {
                throw new Error("Report Requests is in an invalid status.")
            }

            body = this.renderRequestReportForm(this.state.requesteeCompany);
        }
        else if (this.waitingForApiFeedback()) {
            body = this.renderStatusMessage();
        }
        else {
            throw new Error("Report Requests is in an invalid status.")
        }

        return (
            <div id="request-reports-root">
                <div className="section-separator"></div>
                <div className="container">
                    <div id="content-container" className="row row-no-gutters">
                        <div className="col-md-6 col-md-push-6">
                            {body}
                        </div>
                        <div className="col-md-6 col-md-pull-6">
                            <div id="upsell-panel">
                                <div className="logo-container">
                                    <img src="/images/homepage/sign-up-toxnot-logo.svg" />
                                </div>
                                <p>
                                    {this.localizeString("Get Started Easily:")}
                                </p>
                                <div className="bullets-container">
                                    <div className="bullet">
                                        <div className="icon">
                                            <img src="/images/homepage/sign-up-bullet-3e.svg" />
                                        </div>
                                        <div className="content">
                                            <div className="title">
                                                {this.localizeString("Find Product & Material Data on the Passport Library")}
                                            </div>
                                            <div className="blurb">
                                                <div style={{ marginBottom: "5px" }}>
                                                    {this.localizeString("Search our database of 1,000's of materials. Send unlimited Supplier Surveys.")}
                                                </div>
                                                <div>
                                                    <a href="/" target="_blank">{this.localizeString("Learn more about 3E Exchange & Sign Up for Free")}</a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private waitingForApiFeedback() {
        return this.state.status === RequestReportStatus.RequestSending || this.state.status === RequestReportStatus.RequestSent || this.state.status === RequestReportStatus.RequestFailed;
    }

    // ===============================================================================

    private renderStatusMessage() {
        const status = this._statusToToxnotStatusComponentStateMap[this.state.status];
        return (
            <ToxnotReactStatusComponent
                appServices={this.props.appServices}
                state={status.state}
                localizedOperationMessage={status.message}
            />
        );
    }

    // ===============================================================================

    private renderLoading() {
        return (
            <div className="text-center">
                <LoadingSpinner size={LoadingSpinnerSize.Medium} />
            </div>
        );
    }

    // ===============================================================================

    private renderNoCompanyFoundMessage() {
        return (
            <div className="text-center">
                {this.localizeString("Sorry, looks like the link you were given is no longer valid. Please request a new link.")}
            </div>
        );
    }

    // ===============================================================================

    private renderRequestReportForm(company: SharedMaterialsFullCompany) {
        return (
            <div id="form-container">
                <div className="header">
                    {company.logoFileId !== null &&
                        <div className="company-logo-container">
                            <img src={`/Api/GeneralFileImage?fileId=${encodeURIComponent(company.logoFileId)}&overrideTeamOwnerId=${company.ownerId}`} />
                        </div>
                    }
                    <div className="title">
                        {company.name}
                    </div>
                </div>
                <div className="info header-info">
                    <p>
                        {this.localizeString("Provide your contact info & select the reports you need.")}
                    </p>
                    <p>
                        {this.localizeString("We'll follow up soon!")}
                    </p>
                </div>
                <div className="form">
                    <div className="form-group contact-form-group">
                        <input id="contact-name" className="form-control" placeholder={this.localizeString("Your Name")} />
                        <input id="contact-email" className="form-control" placeholder={this.localizeString("Your Email")} />
                    </div>
                    <div className="form-group">
                        <input id="company-name" className="form-control" placeholder={this.localizeString("Your Company Name")} />
                    </div>
                    <div className="form-group">
                        <input id="factory-name" className="form-control" placeholder={this.localizeString("Concerning Factory (optional)")} />
                    </div>
                    <div className="checkbox">
                        <label>
                            <input id="all-materials-reported" type="checkbox" onChange={this.reportOnAllPurchases_onChange} checked={this.state.reportOnAllPurchases} /> {this.localizeString("I want a report on all parts I purchase from") + " " + company.name}
                        </label>
                    </div>
                    <div className="form-group">
                        <textarea id="material-names-input" className="form-control" rows={4} placeholder={this.localizeString("Comma separated part names")} disabled={this.state.reportOnAllPurchases} />
                    </div>
                    <div className="report-types-selection-container">
                        <ReportTypeSelector
                            renderOptions={{ onlyAllowSingleSelection: false, showListInput: true, showRegulations: true }}
                            reportTypesAndContexts={ReportTypesManager.getReportTypesAndContextFromReportTypes(company.reports, this.state.reportTypesToRegulations)}
                            appServices={this.props.appServices}
                            reportSelectedCallback={this.onReportTypeOrRegulationSelect}
                            isFreeUser={false}
                            dynRegs={this.state.dynRegs}
                        />
                    </div>
                    <button className="btn btn-toxnot-primary submit-button" onClick={e => this.requestReportSubmit_click()}>{this.localizeString("Request Reports")}</button>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private getReportTypesManager() {
        if (this._reportTypesManager === null) {
            throw new Error("Report Types Manager was null.");
        }

        return this._reportTypesManager;
    }

    // ===============================================================================

    private getCompanyFromUrlName = async () => {
        const apiResult = await this._serviceClient.getCompanyFromCustomUrlName(this.props.match.params.customUrlName);

        if (!apiResult.success) {
            this.props.appServices.displayApiErrorMessage();
            return;
        }

        let status: RequestReportStatus;

        if (apiResult.data === null) {
            this.props.appServices.displayApiErrorMessage();
            return;
        }

        if (apiResult.data.company == null) {
            status = RequestReportStatus.CompanyNotFound;
        }
        else {
            status = RequestReportStatus.CompanyFound;
        }

        this.setState({
            status: status,
            requesteeCompany: apiResult.data.company,
            reportTypesToRegulations: apiResult.data.reportTypesToRegulations,
            dynRegs: apiResult.data.dynRegs
        })
    }

    // ===============================================================================

    private inputVal = (cssSelector: string) => $(cssSelector).val()?.toString().trim() ?? "";

    // ===============================================================================

    private getMaterialsNames() {
        const materialNamesString = this.inputVal("#material-names-input");
        return materialNamesString.split(",").filter(m => m.trim().length > 0).map(m => m.trim());
    }

    // ===============================================================================

    private onReportTypeOrRegulationSelect = (selectedReports: {[reportType: string]: ReportContext | null}, reportTypesAndRegulationsToSpecificInfo: { [reportTypeOrRegulation: string]: string }) => {
        this.setState({
            selectedReportTypes: selectedReports,
            reportTypesAndRegulationsToSpecificInfo: { ...reportTypesAndRegulationsToSpecificInfo }
        });
    }

    // ===============================================================================

    private getAndVerifyRequestData(): CreateReportRequest | null {
        const companyName = this.inputVal("#company-name");
        const contactName = this.inputVal("#contact-name");
        const contactEmail = this.inputVal("#contact-email");
        const factoryName = this.inputVal("#factory-name");

        if (companyName === "" || contactName === "" || contactEmail === "") {
            alert(this.localizeString("You must provide a company name, contact name, and email."));
            return null;
        }

        const materialNamesToUse = this.getMaterialsNames();

        if (materialNamesToUse.length === 0 && !this.state.reportOnAllPurchases) {
            alert(this.localizeString("You must provide at least one part name."));
            return null;
        }

        else if (Object.keys(this.state.selectedReportTypes).length === 0 ) {
            alert(this.localizeString("You must select at least one report type."));
            return null;
        }

        if (this.state.requesteeCompany == null) {
            throw new Error("Receiving Company was null.");
        }

        const request: CreateReportRequest = {
            ownerIdReceivingRequest: this.state.requesteeCompany.ownerId,
            companyIdReceivingRequest: this.state.requesteeCompany.companyId,
            companyName: companyName,
            contactName: contactName,
            contactEmail: contactEmail,
            factoryName: factoryName,
            materialNames: materialNamesToUse,
            openRequest: this.state.reportOnAllPurchases,
            reportTypes: this.getReportTypesManager().getReportTypesWithContexts(this.state.selectedReportTypes),
            shouldNotifyAndCreateTaskForRequest: true,
            reportTypesAndRegulationsToSpecificInfo: this.state.reportTypesAndRegulationsToSpecificInfo
        };

        return request;
    }

    // ===============================================================================
    //
    // UI Events
    //

    private requestReportSubmit_click = async () => {
        const request = this.getAndVerifyRequestData();

        // User did not provide all the necessary data.
        if (request === null) {
            return;
        }

        this.setState({
            status: RequestReportStatus.RequestSending
        });

        const apiResult = await this._serviceClient.createCustomerReportRequest(request);

        if (!apiResult.success) {
            this.props.appServices.displayApiErrorMessage();

            this.setState({
                status: RequestReportStatus.RequestFailed
            });

            return;
        }

        this.setState({
            status: RequestReportStatus.RequestSent
        });
    }

    // ===============================================================================

    private reportOnAllPurchases_onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            reportOnAllPurchases: e.target.checked
        });
    }
}

// ===============================================================================

export default connect(
    (state: ApplicationState) => ({ appServices: state.appServices })
)(RequestReports as any);